import React, { useMemo, useState } from 'react'
import { CirclePlus } from 'lucide-react'
import { CustomAlert, InputField, PageTitle, RegularButton, SpinnerIcon } from '../../atoms'
import BackButton from '../../atoms/back-button'
import ActionDialog, { LoadingLabel } from '../../molecules/action-dialog'
import { DashboardWizardStyled } from "./styled";
import { useAvailableDashboards, useClientDashboards } from '../../../api/queries/useDashboard'
import {useAppContext} from "../../../context/appContext";
import {assignPanelToClient, createPanel} from "../../../api";
import routes from "../../../config/routes";
import {removeQueriesByKeys} from "../../../api/mutations/mutationCallbacks";
import { ALERT_LEVEL, MODULES, QUERY_KEYS } from '../../../config/constants'
import { useHistory } from 'react-router-dom'
import i18n from '../../../assets/i18n'
import { matchPath, useLocation } from 'react-router'

const DashboardWizard = () => {
  const history = useHistory();
  let {module, createAlert} = useAppContext();
  module = module || MODULES.ID_TALENT_ACQUISITION;
  let location = useLocation();
  const isAdminSection = matchPath(location.pathname, {path: routes.adminDashboardWizard});

  const [selectCustomDashboard, setSelectCustomDashboard] = useState(false);
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState({ message: '', severity: '' });
  // Panel prediseñado pendiente de confirmar: antes se añadía al cliente con un solo clic
  const [presetToAdd, setPresetToAdd] = useState(null);

  const {data: preconfiguredDashboards, isLoading} = useAvailableDashboards(module);

  const handleCreateDashboard = () => {
    if (!newDashboardName) {
      setShowAlert(true);
      setAlert({message: i18n.dashboard.wizard.error_name, severity: "error"});
    } else if(!isCreatingDashboard) {
      setIsCreatingDashboard(true);
      createPanel(module, newDashboardName).then((res) => {
        if (res && res.success && res.id) {
          removeQueriesByKeys( [`${module}:${QUERY_KEYS.DASHBOARDS}`]);
          if (isAdminSection) {
            history.push(routes.adminDashboardEdit.replace(':id', res.id));
          } else {
            history.push(routes.dashboardEdit.replace(':module_id', module).replace(':id', res.id));
          }
        }
      }).catch((e) => {
        console.error(e)
        setShowAlert(true);
        setIsCreatingDashboard(false);
        setAlert({message: i18n.dashboard.wizard.error_creating, severity: "error"});
      });
    }
    setTimeout(() => {
      setShowAlert(false);
    }, 3000);
  }

  // Devuelve la promesa: el ActionDialog muestra el spinner y, si va bien, se navega al panel
  const handleConfirmPreset = () => {
    if (!presetToAdd) return;
    return assignPanelToClient(module, presetToAdd.id).then(res => {
      removeQueriesByKeys( [`${module}:${QUERY_KEYS.DASHBOARDS}`]);
      history.push(routes.dashboardHome.replace(':module_id', module).replace(':id', res.id));
    }).catch(e => {
      console.error(e);
      createAlert(i18n.dashboard.wizard.errorAssigning, ALERT_LEVEL.ERROR);
    });
  }

  const translate = (map, key) => (map && map.hasOwnProperty(key) ? map[key] : key)

  const presets = useMemo(() => (preconfiguredDashboards || []).map(dashboard => ({
    id: dashboard.id,
    preview: dashboard.preview_url,
    name: translate(i18n.dashboard.wizard.name, dashboard.name),
    description: translate(i18n.dashboard.wizard.description, dashboard.description),
  })), [preconfiguredDashboards]);

  const { data: clientDashboards, isLoading: isLoadingClientDashboards } = useClientDashboards(module);

  const hasDashboards = !isLoadingClientDashboards && clientDashboards && clientDashboards.length > 0;

  return (
    <DashboardWizardStyled>
      {hasDashboards && (
        <BackButton
          title={i18n.dashboard.wizard.backToPanels}
          onClick={() => history.push(routes.dashboardHome.replace(':module_id', module))}
        />
      )}
      <div className="title">
        <PageTitle title={i18n.dashboard.wizard.title} />
      </div>

      {isLoading ? <SpinnerIcon/> : <>
        <section className="wizard-section">
          <h2 className="section-title">{i18n.dashboard.wizard.presetsTitle}</h2>
          <p className="section-copy">{i18n.dashboard.wizard.presetsCopy}</p>
          <div className="preset-grid">
            {presets.map(preset => (
              <button key={preset.id} type="button" className="preset-card" onClick={() => setPresetToAdd(preset)}>
                <span className="preset-image">{preset.preview && <img src={preset.preview} alt="" />}</span>
                <span className="preset-name">{preset.name}</span>
                <span className="preset-description">{preset.description}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="wizard-section">
          <h2 className="section-title">{i18n.dashboard.wizard.customTitle}</h2>
          <p className="section-copy">{i18n.dashboard.wizard.customCopy}</p>
          <div className={`custom-card ${selectCustomDashboard ? 'active' : ''}`}>
            {!selectCustomDashboard ? (
              <button type="button" className="custom-trigger" onClick={() => setSelectCustomDashboard(true)}>
                <CirclePlus size={22} />
                <span>{i18n.dashboard.wizard.new_from_scratch}</span>
              </button>
            ) : (
              <form className="custom-form" onSubmit={e => { e.preventDefault(); handleCreateDashboard(); }}>
                <InputField name="name" placeholder={i18n.dashboard.wizard.placeholder_custom_dashboard} value={newDashboardName} handleOnChange={setNewDashboardName}/>
                <RegularButton
                  type="submit"
                  disabled={isCreatingDashboard}
                  title={<LoadingLabel loading={isCreatingDashboard}>{i18n.dashboard.wizard.create}</LoadingLabel>}
                />
              </form>
            )}
            {showAlert && <CustomAlert severity={alert.severity} message={alert.message}/>}
          </div>
        </section>
      </>}

      <ActionDialog
        open={!!presetToAdd}
        title={presetToAdd ? i18n.dashboard.wizard.confirmAddTitle.replace('{name}', presetToAdd.name) : ''}
        onClose={() => setPresetToAdd(null)}
        onConfirm={handleConfirmPreset}
        cancelLabel={i18n.dialog.decline}
        confirmLabel={i18n.dashboard.wizard.confirmAddButton}
      />
    </DashboardWizardStyled>
  )
}

export default DashboardWizard
